'use client'

import { useMemo } from 'react'
import { GiftIcon, type InventoryItemData } from '@repo/ui'
import AngleLeftIcon from 'src/app/assets/icons/ico-angle-left.svg'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage, useGetCurrentGameId, useGetCurrentGame } from 'src/store/game'
import { isMarbleGame } from 'src/types/domain/Game'
import { useClaimedFreeBetsForGame, useClaimedBoosters } from 'src/features/inventory/store'
import { useInventoryItemActions } from 'src/features/inventory/hooks'
import { gameSupportsFreeBets } from 'src/features/inventory/utils/gameSupportsFreeBets'
import { groupFreeBets } from 'src/features/inventory/utils/groupFreeBets'
import { isItemApplicableToGame } from 'src/features/inventory/utils/isItemApplicableToGame'
import { getApplicableGamesText } from 'src/features/inventory/utils/getApplicableGamesText'
import { mapFreeBetGroupToItemData } from 'src/features/inventory/mappers/freeBetGroup.mapper'
import { mapBoosterToItemData } from 'src/features/inventory/mappers/inventoryItem.mapper'

type Props = Readonly<{ onBackClick?: () => void }>
type Accent = NonNullable<InventoryItemData['accentColor']>

// Brand accent tokens (shared from @repo/ui): green for free bets, blue for boosters.
const accentTopLine: Record<Accent, string> = {
  green: 'shadow-green-top-accent',
  blue: 'shadow-blue-top-accent',
  yellow: 'shadow-yellow-top-accent',
  none: ''
}
const accentIconBg: Record<Accent, string> = {
  green: 'bg-dark-green',
  blue: 'bg-blue',
  yellow: 'bg-yellow',
  none: 'bg-white/10'
}
const accentValue: Record<Accent, string> = {
  green: 'text-green',
  blue: 'text-blue',
  yellow: 'text-yellow',
  none: 'text-white'
}

/**
 * Sidebar "Rewards" view: held free bets (grouped, read-only — they apply
 * automatically) and boosters (with their existing activation). A simple
 * vertical list so it fits the narrow sidebar and stacks cleanly on mobile.
 * Replaces the in-bet inventory backpack.
 */
export function RewardsSidebarPanel({ onBackClick }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const currentGameId = useGetCurrentGameId()
  const currentGame = useGetCurrentGame()
  // `track.name` is the display name ("Marble Plinko"); `game.name` is the configuration ("Classic").
  const gameName = isMarbleGame(currentGame) ? currentGame.track.name : undefined
  // v12.4 ask 6: free bets are hidden on crash games — the backend
  // hard-rejects wagering them there, so listing them (with an activate
  // action nothing on a crash page consumes) is dead-end UX. They reappear
  // the moment the player opens a free-bet-capable game.
  const supportsFreeBets = gameSupportsFreeBets(
    isMarbleGame(currentGame) ? currentGame.track.gameType : undefined
  )
  const claimedFreeBets = useClaimedFreeBetsForGame(currentGameId)
  const freeBets = supportsFreeBets ? claimedFreeBets : []
  const boosters = useClaimedBoosters()
  const { handleActivateItem, handleActivateBonus, handleDeactivateItem } =
    useInventoryItemActions()

  const items = useMemo<InventoryItemData[]>(() => {
    const freeBetItems = groupFreeBets(freeBets).map((group) =>
      mapFreeBetGroupToItemData(group, t, gameName)
    )

    const boosterItems = boosters.map((booster) => {
      const isApplicable = currentGameId ? isItemApplicableToGame(booster, currentGameId) : true
      return mapBoosterToItemData(booster, {
        t,
        applicableGamesText: getApplicableGamesText(booster, isApplicable, t),
        isApplicableToGame: isApplicable,
        onActivateItem: handleActivateItem,
        onActivateBonus: handleActivateBonus,
        onDeactivateItem: handleDeactivateItem
      })
    })

    return [...freeBetItems, ...boosterItems]
  }, [
    freeBets,
    boosters,
    currentGameId,
    gameName,
    t,
    handleActivateItem,
    handleActivateBonus,
    handleDeactivateItem
  ])

  return (
    <div className="rewards-panel">
      <div className="panel-header">
        {onBackClick ? (
          <button className="back" onClick={onBackClick} type="button">
            <span className="icon">
              <AngleLeftIcon />
            </span>
          </button>
        ) : null}
        <GiftIcon />
        <span>{t('Rewards')}</span>
      </div>

      <div className="panel-content">
        <ul className="flex flex-col gap-2.5">
          {items.map((item) => {
            const accent = item.accentColor ?? 'none'
            return (
              <li
                className={`flex flex-col gap-2 rounded-xl bg-glass-overlay-light bg-primary-background/80 p-3 ${accentTopLine[accent]}`}
                key={item.id}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg shadow-white-top-accent-inset [&>svg]:!h-[18px] [&>svg]:!w-[18px] ${accentIconBg[accent]}`}
                  >
                    {item.icon}
                  </span>
                  <span className="flex-1 truncate font-formula font-bold text-white">
                    {item.name}
                  </span>
                  <span
                    className={`shrink-0 font-formula text-lg font-bold ${accentValue[accent]}`}
                  >
                    {item.quantity}
                  </span>
                </div>

                {item.description ? (
                  <p className="whitespace-pre-line text-xs leading-snug text-white/50">
                    {item.description}
                  </p>
                ) : null}

                {item.footer ? (
                  <div className="overflow-hidden rounded-lg">{item.footer}</div>
                ) : null}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
